import { useState, FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { validateEmail } from '../utils/validation';
import { isValidPhone, normalizePhone } from '../utils/phone';

export const SignUp = () => {
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!fullName.trim()) {
      setError('Full name is required');
      return;
    }
    if (!validateEmail(email)) {
      setError('Enter a valid email');
      return;
    }
    if (phone && !isValidPhone(phone)) {
      setError('Enter a valid mobile number');
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    if (!termsAccepted) {
      setError('You must agree to the Terms & Conditions and Privacy Policy to continue');
      return;
    }

    const normalizedPhone = phone ? normalizePhone(phone) : null;
    setIsSubmitting(true);

    try {
      const { data, error: signUpError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: fullName.trim(),
            phone: normalizedPhone,
            terms_accepted: true,
          },
        },
      });
      if (signUpError) throw signUpError;
      if (!data.user) throw new Error('Sign-up failed');

      // Profile is created by the auth.users trigger (0013_signup_profile_trigger.sql).
      // Clear the auto-created session; user must be approved before login.
      await supabase.auth.signOut();
      setSubmitted(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sign-up failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputClass = 'w-full rounded border border-gray-300 px-3 py-2 text-sm';

  if (submitted) {
    return (
      <div className="mx-auto max-w-md rounded-lg border border-green-200 bg-green-50 p-6 shadow-sm text-green-800">
        <h1 className="mb-2 text-xl font-bold">Registration Submitted</h1>
        <p className="mb-4 text-sm">Your account is under review. An employee will approve it shortly.</p>
        <button
          onClick={() => navigate('/login', { replace: true })}
          className="w-full rounded bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700"
        >
          Go to Login
        </button>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-md rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
      <h1 className="mb-4 text-xl font-bold text-gray-900">Sign Up</h1>
      {error && <p className="mb-3 text-sm text-red-600">{error}</p>}
      <form onSubmit={handleSubmit} className="space-y-4">
        <input
          type="text"
          placeholder="Full Name"
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
          className={inputClass}
          required
        />
        <input
          type="email"
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className={inputClass}
          required
        />
        <input
          type="tel"
          placeholder="Mobile number (optional)"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          className={inputClass}
        />
        <input
          type="password"
          placeholder="Password (min 6)"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className={inputClass}
          required
          minLength={6}
        />
        <label className="flex items-start gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={termsAccepted}
            onChange={(e) => setTermsAccepted(e.target.checked)}
            className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600"
            required
          />
          <span>
            I agree to the Terms & Conditions and{' '}
            <Link to="/privacy-policy" className="font-medium text-blue-600 hover:text-blue-800">
              Privacy Policy
            </Link>
            . I understand that my information, including my mobile number, may be used to create and
            manage my Phone Book account and provide authorized Phone Book services. I understand that
            my registration is subject to approval.
          </span>
        </label>
        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
        >
          {isSubmitting ? 'Creating account...' : 'Sign Up'}
        </button>
      </form>
      <p className="mt-4 text-center text-sm text-gray-600">
        Already have an account?{' '}
        <Link to="/login" className="font-medium text-blue-600 hover:text-blue-800">
          Login
        </Link>
      </p>
    </div>
  );
};
